import { consume } from '@lit/context';
import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { breakpointContext } from '../contexts/breakpoint';
import { BREAKPOINT } from '../util/breakpoints';
import { sourceContext } from '../contexts/source';
import { SOURCE } from '../util/source';

@customElement('rq-exit-modal')
export class ConfirmExitModal extends LitElement {
    @state()
    private showModal = false;

    @consume({ context: sourceContext, subscribe: true })
    source!: SOURCE;

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    get isIphone() {
        return /iphone/i.test(navigator.userAgent);
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.isIphone) {
            document.addEventListener(
                'touchend',
                () => {
                    window.history.pushState(null, '', window.location.href);
                },
                { once: true }
            );
        } else {
            window.history.pushState(null, '', window.location.href);
        }
        window.addEventListener('popstate', this.handlePopState);

        this.addEventListener('lh-hide', this.handleModalHide);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.isIphone) {
            document.removeEventListener('touchend', () => {
                history.pushState(null, '', window.location.href);
            });
        }
        window.removeEventListener('popstate', this.handlePopState);
        this.removeEventListener('lh-hide', this.handleModalHide);
    }

    handleOnShow = () => {
        this.showModal = true;
    };

    handlePopState = () => {
        this.showModal = true;
    };

    handleModalHide = (e: Event) => {
        e.preventDefault();
        this.cancelExit();
    };

    confirmExit() {
        this.showModal = false;
        window.history.back();
    }

    cancelExit() {
        this.showModal = false;
        window.history.pushState(null, '', window.location.href);
    }

    render() {
        return html`
            <lh-modal open=${this.showModal || nothing} id="exit-modal" accessible-label="Exit confirmation" width="sm">
                <lh-typography type="heading" size="xl" selector=${this.isMobile ? 'h3' : 'h5'}>
                    Are you sure you want to leave?
                </lh-typography>
                <lh-typography>You'll lose all progress you've made on this quote.</lh-typography>
                <lh-button-group alignment="auto">
                    <lh-button
                        analytics-id="rate-quote:${this.source}:u65:exit-modal:exit-quote:button-text"
                        variant="text"
                        @click=${this.confirmExit}
                        >Exit Quote</lh-button
                    >
                    <lh-button
                        analytics-id="rate-quote:${this.source}:u65:exit-modal:continue-quote:button-filled"
                        variant="filled"
                        @click=${this.cancelExit}
                        >Continue Quote</lh-button
                    >
                </lh-button-group>
            </lh-modal>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-exit-modal': ConfirmExitModal;
    }
}
